/* eslint-disable react/no-multi-comp */
/* eslint-disable react/display-name */
import { lazy } from 'react';
import { Redirect } from 'react-router-dom';
import AuthLayout from './shared/components/layouts/Auth';
import ErrorLayout from './shared/components/layouts/Error';
import DashboardLayout from './shared/components/layouts/Dashboard';
import DashboardDefaultView from './modules/DashboardDefault';
import EditEmployee from './modules/EditEmployee';
import EmployeeDetails from './modules/Flexflow/EmployeeDetails';
import Integrations from './modules/Settings/Integrations';
import { SaldoView } from './modules/SaldoView';
import { isTestingEnvironment } from './shared/utils/isTestingEnvironment';

export const ssoConfig = [
  { hostname: 'correos.payflow.es', image: '/images/sso/correos.png' },
  { hostname: 'correos.payflow-dev.net', image: '/images/sso/correos.png' }
];

export default initialPage => {
  // Check if the current domain matches any SSO configuration
  const currentSSOConfig =
    typeof window !== 'undefined' ? ssoConfig.find(config => window.location.hostname === config.hostname) : null;

  // If on SSO domain, return restricted routes that only allow SSO login
  if (currentSSOConfig) {
    return [
      {
        path: '/',
        exact: true,
        component: AuthLayout,
        routes: [
          {
            path: '/',
            exact: true,
            component: lazy(() => import('src/modules/Login/SSOLogin/SSOLogin'))
          }
        ]
      },
      {
        path: '*',
        component: () => <Redirect to={'/'} />
      }
    ];
  }

  // Return full routes for other domains
  return [
    {
      path: '/',
      exact: true,
      component: () => <Redirect to={initialPage} />
    },
    {
      path: '/auth',
      component: AuthLayout,
      routes: [
        {
          path: '/auth/login',
          exact: true,
          component: lazy(() => import('src/modules/Login/AdminLogin/AdminLogin'))
        },
        {
          path: '/auth/recover',
          exact: true,
          component: lazy(() => import('src/modules/Login/PasswordReset/PasswordResetRequest'))
        },
        {
          path: '/auth/password-expired',
          exact: true,
          component: lazy(() => import('src/modules/Login/PasswordReset/PasswordExpired'))
        },
        {
          path: '/auth/reset',
          exact: true,
          component: lazy(() => import('src/modules/Login/PasswordReset/PasswordReset'))
        },
        {
          path: '/auth/super/login',
          exact: true,
          component: lazy(() => import('src/modules/Login/SuperAdminLogin/SuperAdminLogin'))
        },
        {
          path: '/auth/sso/callback',
          exact: true,
          component: lazy(() => import('src/modules/Login/SSOCallback/SSOCallback'))
        },
        {
          component: () => <Redirect to="/errors/error-404" />
        }
      ]
    },
    {
      path: '/errors',
      component: ErrorLayout,
      routes: [
        {
          path: '/errors/error-401',
          exact: true,
          component: lazy(() => import('src/modules/Generic/pages/Error401'))
        },
        {
          path: '/errors/error-404',
          exact: true,
          component: lazy(() => import('src/modules/Generic/pages/Error404'))
        },
        {
          path: '/errors/error-500',
          exact: true,
          component: lazy(() => import('src/modules/Generic/pages/Error500'))
        },
        {
          component: () => <Redirect to="/errors/error-404" />
        }
      ]
    },
    {
      path: '/super',
      component: DashboardLayout,
      routes: [
        {
          path: '/super/list-super-admins',
          exact: true,
          component: lazy(() => import('src/modules/superAdmin/SuperAdminList'))
        },
        {
          path: '/super/payments',
          component: lazy(() => import('src/modules/superAdmin/PaymentsList'))
        },
        {
          path: '/super/dummify',
          component: lazy(() => import('src/modules/superAdmin/Operations/Dummify'))
        },
        {
          path: '/super/emergency',
          component: lazy(() => import('src/modules/superAdmin/Operations/Emergency'))
        },
        {
          path: '/super/post-cycle-transactions',
          component: lazy(() =>
            import(
              'src/modules/superAdmin/PostCycleTransactions/pages/PostCycleTransactionsList/PostCycleTransactionsList'
            )
          )
        },
        {
          path: '/super/manage-transactions',
          component: lazy(() => import('src/modules/superAdmin/Operations/ManageTransactionPage'))
        },
        {
          path: '/super/global-limits',
          component: lazy(() => import('src/modules/superAdmin/Territory/pages/Territory'))
        },
        {
          path: '/super/holding/:holdingId/hub',
          component: lazy(() => import('src/modules/superAdmin/Hub/pages/HoldingHub'))
        },
        {
          path: '/super/change/personal-id',
          component: lazy(() => import('src/modules/superAdmin/Operations/ChangePersonalIdPage'))
        },
        {
          path: '/super/alerts',
          component: lazy(() => import('src/modules/superAdmin/Alert/pages/AlertsList'))
        },
        {
          path: '/super/alert/create',
          component: lazy(() => import('src/modules/superAdmin/Alert/pages/CreateAlert'))
        },
        {
          path: '/super/alert/:id',
          component: lazy(() => import('src/modules/superAdmin/Alert/pages/EditAlert'))
        },
        {
          path: '/super/holdings',
          component: lazy(() => import('src/modules/superAdmin/Holding/pages/HoldingsList'))
        },
        {
          path: '/super/holding/create',
          component: lazy(() => import('src/modules/superAdmin/Holding/pages/CreateHolding'))
        },
        {
          path: '/super/holding/:holdingId/create-company',
          component: lazy(() => import('src/modules/superAdmin/Company/pages/CreateCompany'))
        },
        {
          path: '/super/holding/:holdingId/create-contact',
          component: lazy(() => import('src/modules/superAdmin/Contact/pages/CreateContact'))
        },
        {
          path: '/super/contact/:contactId',
          component: lazy(() => import('src/modules/superAdmin/Contact/pages/ViewContact'))
        },
        {
          path: '/super/company-v2/:companyId',
          component: lazy(() => import('src/modules/superAdmin/Company/pages/ViewCompany'))
        },
        {
          path: '/super/holding/:holdingId',
          component: lazy(() => import('src/modules/superAdmin/Holding/pages/ViewHolding'))
        },
        {
          path: '/super/hub/territories',
          component: lazy(() => import('src/modules/superAdmin/Hub/pages/TerritoryHub'))
        },
        ...(isTestingEnvironment()
          ? [
              {
                path: '/super/sandbox/payments',
                component: lazy(() => import('src/modules/superAdmin/Sandbox/pages/SandboxPayments'))
              }
            ]
          : [])
      ]
    },
    {
      route: '*',
      component: DashboardLayout,
      routes: [
        {
          path: '/saldo',
          heroOnly: true,
          exact: true,
          component: SaldoView
        },
        {
          path: '/overview',
          exact: true,
          component: DashboardDefaultView
        },
        {
          path: '/data-review',
          exact: true,
          component: lazy(() => import('src/modules/DataReview'))
        },
        {
          path: '/history',
          exact: true,
          component: lazy(() => import('src/modules/History'))
        },
        {
          path: '/add-employee',
          exact: true,
          component: lazy(() => import('src/modules/ManageEmployee'))
        },
        {
          path: '/add-employee-choice',
          exact: true,
          component: lazy(() => import('src/modules/AddEmployeesChoice'))
        },
        {
          path: '/employee/inactivate',
          exact: true,
          component: lazy(() => import('src/modules/InactivateContracts'))
        },
        {
          path: '/edit-employee/:personalId',
          exact: true,
          render: routeObj => <EditEmployee personalId={routeObj.match.params.personalId} />
        },
        {
          path: '/employee/restricted',
          component: lazy(() => import('src/modules/Blacklist'))
        },
        {
          path: '/list-employees',
          exact: true,
          component: lazy(() => import('src/modules/EmployeeList'))
        },
        {
          path: '/collectives',
          heroOnly: true,
          exact: true,
          component: lazy(() => import('src/modules/Collectives'))
        },
        {
          path: '/list-admins',
          exact: true,
          component: lazy(() => import('src/modules/Admin/pages/AdminsList'))
        },
        {
          path: '/list-admins/create',
          exact: true,
          component: lazy(() => import('src/modules/Admin/pages/CreateAdmin'))
        },
        {
          path: '/list-admins/:adminId/edit',
          exact: true,
          component: lazy(() => import('src/modules/Admin/pages/EditAdmin'))
        },
        {
          path: '/import-file',
          exact: true,
          component: lazy(() => import('src/modules/UploadFile'))
        },
        {
          path: '/liquidation-list',
          exact: true,
          component: lazy(() => import('src/modules/LiquidationList/LiquidationList'))
        },
        {
          path: '/payroll',
          exact: true,
          component: lazy(() => import('src/modules/Payroll'))
        },
        {
          path: '/settings/configuration/extra-payment/create',
          heroOnly: true,
          exact: true,
          component: lazy(() => import('src/modules/ExtraPayment/pages/CreateExtraPayment'))
        },
        {
          path: '/settings/configuration/extra-payment/:extraPaymentId/edit',
          heroOnly: true,
          exact: true,
          component: lazy(() => import('src/modules/ExtraPayment/pages/EditExtraPayment'))
        },
        {
          path: '/settings/configuration',
          exact: true,
          component: lazy(() => import('src/modules/Settings/Configuration'))
        },
        {
          path: '/settings/integrations',
          heroOnly: true,
          exact: true,
          component: lazy(() => import('src/modules/Settings/Integrations'))
        },
        {
          path: '/settings/integrations/:integrationName',
          heroOnly: true,
          exact: true,
          render: routeObj => <Integrations integrationNameSource={routeObj.match.params.integrationName} />
        },
        {
          path: '/settings/sftp',
          heroOnly: true,
          exact: true,
          component: lazy(() => import('src/modules/Settings/SFTP'))
        },
        {
          path: '/settings/password',
          exact: true,
          component: lazy(() => import('src/modules/Settings/Password'))
        },
        {
          path: '/settings/personalization',
          heroOnly: true,
          exact: true,
          component: lazy(() => import('src/modules/superAdmin/Personalization'))
        },
        {
          path: '/settings/companies',
          exact: true,
          component: lazy(() => import('src/modules/superAdmin/Subsidiary/SubsidiaryList'))
        },
        {
          path: '/settings/help',
          exact: true,
          component: lazy(() => import('src/modules/Settings/Help'))
        },
        // {
        //   path: '/flexflow/liquidation-list',
        //   exact: true,
        //   component: lazy(() => import('src/modules/LiquidationList/FlexflowLiquidationList'))
        // },
        {
          path: '/flexflow/employees',
          exact: true,
          component: lazy(() => import('src/modules/Flexflow/Contracts/Contracts'))
        },
        {
          path: '/flexflow/employees/:payflowContractId',
          exact: true,
          render: routeObj => <EmployeeDetails payflowContractId={routeObj.match.params.payflowContractId} />
        },
        {
          path: '/flexflow/history',
          exact: true,
          component: lazy(() => import('src/modules/Flexflow/History'))
        },
        {
          path: '/flexflow/configuration',
          exact: true,
          component: lazy(() => import('src/modules/Flexflow/Configuration'))
        },
        {
          path: '/holding/flexflow/application',
          exact: true,
          component: lazy(() => import('src/modules/Flexflow/Applications'))
        },
        {
          path: '/holding/flexflow/application/nursery/:uuid',
          component: lazy(() =>
            import('src/modules/Flexflow/Applications/pages/ViewApplication/ViewNurseryApplication')
          )
        },
        {
          path: '/holding/flexflow/application/education/:uuid',
          component: lazy(() =>
            import('src/modules/Flexflow/Applications/pages/ViewApplication/ViewEducationApplication')
          )
        },
        {
          path: '/holding/flexflow/application/insurance/:uuid',
          component: lazy(() =>
            import('src/modules/Flexflow/Applications/pages/ViewApplication/ViewInsuranceApplication')
          )
        },
        {
          path: '/logout',
          exact: true,
          component: lazy(() => import('src/modules/Generic/pages/AvaliableSoon'))
        },
        {
          path: ['/feature-disabled', '/feature-disabled/:feature'],
          exact: true,
          component: lazy(() => import('src/modules/Generic/pages/FeatureDisabled'))
        },
        {
          component: () => <Redirect to="/errors/error-404" />
        }
      ]
    }
  ];
};
